// Открытые вопросы к задачам: счётчики, повестка, запись. Спека — docs/superpowers/specs/2026-09-08-open-questions-design.md
import { useMemo } from 'react';
import { data, useTable } from '@core/store';
import type { Change, Row } from '@core/store/model';
import { actorLabel } from '@core/people';

export type Question = Row<'question'>;
export type Answer = Row<'answer'>;

/** Открытых вопросов по задаче. */
export function openCountByTask(questions: Iterable<Pick<Question, 'task_id' | 'status'>>): Map<string, number> {
  const m = new Map<string, number>();
  for (const q of questions) if (q.status === 'open' && q.task_id) m.set(q.task_id, (m.get(q.task_id) ?? 0) + 1);
  return m;
}

export type AgendaGroup<T> = { project_id: string; name: string; tasks: { task: T; questions: Question[] }[] };
export const agendaCount = <T,>(g: AgendaGroup<T>): number => g.tasks.reduce((n, t) => n + t.questions.length, 0);

const resolvedAt = (q: Question) => q.closed_at ?? q.updated_at;

/**
 * Повестка по проектам в их порядке. Открытые: задачи по дате начала, вопросы по дате создания —
 * порядок роадмапа сверху вниз. Решённые: задачи и вопросы по дате решения, свежие сверху — их много.
 */
export function groupAgenda<T extends { id: string; project_id: string | null; start_date: string }>(
  questions: Iterable<Question>, tasks: Map<string, T>, projects: { id: string; name: string }[], status: 'open' | 'closed' = 'open',
): AgendaGroup<T>[] {
  const byTask = new Map<string, Question[]>();
  for (const q of questions) {
    if (q.status !== status || !q.task_id || !tasks.has(q.task_id)) continue;
    byTask.set(q.task_id, [...(byTask.get(q.task_id) ?? []), q]);
  }
  const sortQs = (qs: Question[]) => status === 'open' ? qs.sort((a, b) => a.created_at.localeCompare(b.created_at)) : qs.sort((a, b) => resolvedAt(b).localeCompare(resolvedAt(a)));
  const groups: AgendaGroup<T>[] = [];
  for (const p of projects) {
    const inProject = [...byTask.entries()].map(([id, qs]) => ({ task: tasks.get(id)!, questions: sortQs(qs) }))
      .filter((x) => x.task.project_id === p.id)
      .sort((a, b) => status === 'open' ? a.task.start_date.localeCompare(b.task.start_date) : resolvedAt(b.questions[0]!).localeCompare(resolvedAt(a.questions[0]!)));
    if (inProject.length) groups.push({ project_id: p.id, name: p.name, tasks: inProject });
  }
  return groups;
}

export function useOpenQuestionCounts(): Map<string, number> {
  const questions = useTable('question');
  return useMemo(() => openCountByTask(questions.values()), [questions]);
}

/** Автор для аватара: человек (цвет и картинка его аккаунта, если привязан), иначе записавший аккаунт. */
export function authorOf(
  authorPersonId: string | null, recordedBy: string,
  people: Map<string, Row<'person'>>, profiles: Map<string, Row<'profile'>>,
): { name: string; color: string; avatar: string | null } {
  const person = authorPersonId ? people.get(authorPersonId) : undefined;
  if (!person) return actorLabel(recordedBy, profiles, people);
  const linked = [...profiles.values()].find((pr) => pr.person_id === person.id);
  return { name: person.name, color: person.color, avatar: linked?.avatar_url ?? null };
}

/** «Varya» или «Varya · recorded by Vasilina»: автор — человек, записавший — аккаунт. */
export function askedBy(
  authorPersonId: string | null, recordedBy: string,
  people: Map<string, Row<'person'>>, profiles: Map<string, Row<'profile'>>,
): string {
  const recorder = actorLabel(recordedBy, profiles, people).name;
  const author = authorPersonId ? people.get(authorPersonId)?.name : undefined;
  if (!author || author === recorder) return author ?? recorder;
  return `${author} · recorded by ${recorder}`;
}

const apply = (changes: Change[], kind: string, question_id?: string) => data.applyChanges(changes, { kind, question_id });
const closedBy = (me: string) => ({ status: 'closed', closed_at: new Date().toISOString(), closed_by: me });

export const askQuestion = (task_id: string, text: string, author_person_id: string | null) =>
  apply([{ table: 'question', insert: { task_id, text: text.trim(), author_person_id } }], 'ask_question');

export const answerQuestion = (q: Question, text: string, author_person_id: string | null, close: boolean, me: string) =>
  apply([
    { table: 'answer', insert: { question_id: q.id, text: text.trim(), author_person_id } },
    ...(close ? [{ table: 'question' as const, id: q.id, set: closedBy(me), expect_updated_at: q.updated_at }] : []),
  ], close ? 'answer_and_close' : 'answer', q.id);

export const closeQuestion = (q: Question, me: string) =>
  apply([{ table: 'question', id: q.id, set: closedBy(me), expect_updated_at: q.updated_at }], 'close_question', q.id);

export const removeQuestion = (q: Question) =>
  apply([{ table: 'question', id: q.id, delete: true }], 'delete_question', q.id); // ответы уходят каскадом в базе

export const reopenQuestion = (q: Question) =>
  apply([{ table: 'question', id: q.id, set: { status: 'open', closed_at: null, closed_by: null }, expect_updated_at: q.updated_at }], 'reopen_question', q.id);

export const editQuestionText = (q: Question, text: string) =>
  apply([{ table: 'question', id: q.id, set: { text: text.trim() }, expect_updated_at: q.updated_at }], 'edit_question', q.id);

export const editAnswerText = (a: Answer, text: string) =>
  apply([{ table: 'answer', id: a.id, set: { text: text.trim() }, expect_updated_at: a.updated_at }], 'edit_answer', a.question_id);
